// #539: Blanko-Brief auf dem Briefpapier — Empfänger aus Mieter/Eigentümer/Kontakten, optional
// eine Brief-Vorlage mit gefüllten Platzhaltern, freier Text; Vorschau, PDF-Download, Ablage in der Akte.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DownloadSimple, Eye, FolderSimplePlus, MagnifyingGlass, PencilSimpleLine, SpinnerGap, UserCircle, WarningCircle, X,
} from '@phosphor-icons/react';
import { supabase } from '../../../../lib/supabase/client';
import { DialogHuelle, ImmoButton, ObjektPicker, TextFeld, FeldRaster, type ObjektPickerOption } from '../../../../components/immobilien/ui';
import { briefDateiname, erzeugeBlankoBriefPdf, ladeBriefpapier, type BlankoBriefDaten } from '../../../../lib/dokumente/blankoBrief';
import { vorlageZuBrief } from '../../../../lib/dokumente/briefDaten';
import {
  ladeBriefVorlagen, ladeEmpfaenger, legeBriefAb, sucheEmpfaenger, vorschlagOrdner,
  type BriefVorlageKurz, type EmpfaengerDaten, type EmpfaengerTreffer,
} from '../../../../lib/dokumente/briefApi';
import { ladeObjekte, type AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import { ladeOrdnerBaum, type BaumKnoten } from '../../../../lib/dokumente/aktenschrankApi';
import { flacheOrdner } from '../../../../lib/dokumente/aktenschrank';
import { kategorieLabel } from '../../../../lib/dokumente/briefVorlagen';
import { OrdnerAuswahl } from '../ablage/OrdnerAuswahl';

const heute = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const FELD = {
  width: '100%', padding: '10px 12px', borderRadius: 14, border: 'none', outline: 'none', resize: 'vertical' as const,
  background: 'var(--fill2)', color: 'var(--ink)', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit',
};
const LABEL = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' } as const;
const ART_LABEL = { mieter: 'Mieter', eigentuemer: 'Eigentümer', kontakt: 'Kontakt', dienstleister: 'Dienstleister' } as const;

const grussText = (name: string) => `Sehr geehrte Damen und Herren,\n\n\n\nMit freundlichen Grüßen\n\n${name}${name ? '\n' : ''}Immobilienmanagement Schwede`;

/** Suchfeld für den Empfänger: Treffer aus Mieter, Eigentümer und Kontakten. */
export function EmpfaengerSuche({ onWahl }: { onWahl: (t: EmpfaengerTreffer) => void }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState<EmpfaengerTreffer[]>([]);
  const [sucht, setSucht] = useState(false);
  const lauf = useRef(0);
  useEffect(() => {
    const nr = ++lauf.current;
    if (q.trim().length < 2) { setTreffer([]); setSucht(false); return; }
    setSucht(true);
    const t = setTimeout(() => {
      void sucheEmpfaenger(q).then((r) => { if (nr === lauf.current) { setTreffer(r); setSucht(false); } })
        .catch(() => { if (nr === lauf.current) { setTreffer([]); setSucht(false); } });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative flex flex-col" style={{ gap: 4 }}>
      <div className="flex items-center" style={{ ...FELD, gap: 8, padding: '0 12px', height: 44 }}>
        <MagnifyingGlass size={15} color="var(--ink3)" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mieter, Eigentümer oder Kontakt suchen …" aria-label="Empfänger suchen"
          className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit' }} />
        {sucht && <SpinnerGap size={14} className="animate-spin" color="var(--ink3)" />}
      </div>
      {q.trim().length >= 2 && !sucht && (
        <div className="immo-glas-stark immo-scroll absolute left-0 right-0 flex flex-col overflow-y-auto" role="listbox"
          style={{ top: 48, zIndex: 5, maxHeight: 260, borderRadius: 14, padding: 4, gap: 1 }}>
          {treffer.length === 0 && <span style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ink3)' }}>Nichts gefunden</span>}
          {treffer.map((t) => (
            <button key={`${t.art}-${t.id}`} type="button" role="option" aria-selected={false}
              onClick={() => { onWahl(t); setQ(''); setTreffer([]); }}
              className="immo-hover-flaeche flex flex-col text-left" style={{ minHeight: 44, padding: '6px 10px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
              <span style={{ fontSize: 13.5, fontWeight: 700 }}>{t.name}</span>
              <span className="truncate" style={{ fontSize: 11.5, color: 'var(--ink3)' }}>{t.sub}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function BlankoBriefDialog({ startVorlageId, onSchliessen, onMeldung }: {
  /** Vorlage, mit der der Dialog startet („Als Brief verwenden“ in der Vorlagen-Ansicht). */
  startVorlageId?: string | null;
  onSchliessen: () => void;
  onMeldung: (text: string, fehler?: boolean) => void;
}) {
  const [d, setD] = useState<BlankoBriefDaten>(() => ({ empfaenger: '', ort: 'Kirchhain', datum: heute(), betreff: '', text: grussText('') }));
  const [laeuft, setLaeuft] = useState(false);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [empf, setEmpf] = useState<EmpfaengerDaten | null>(null);
  const [vorlagen, setVorlagen] = useState<BriefVorlageKurz[]>([]);
  const [vorlageId, setVorlageId] = useState('');
  // Ablage
  const [ablage, setAblage] = useState(false);
  const [objekte, setObjekte] = useState<AktObjekt[]>([]);
  const [objektId, setObjektId] = useState<string | null>(null);
  const [baum, setBaum] = useState<BaumKnoten[] | null>(null);
  const [ordnerPfad, setOrdnerPfad] = useState('');

  useEffect(() => () => { if (vorschau) URL.revokeObjectURL(vorschau); }, [vorschau]);
  const setze = (k: keyof BlankoBriefDaten) => (v: string) => setD((x) => ({ ...x, [k]: v }));

  // Eigenen Namen unter den Gruß setzen, solange der Text noch unverändert ist; Vorlagen laden.
  useEffect(() => {
    let lebt = true;
    void (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data: p } = await supabase.from('profiles').select('full_name').eq('id', u.user.id).maybeSingle();
      const name = (p?.full_name ?? '').trim();
      if (lebt && name) setD((x) => (x.text === grussText('') ? { ...x, text: grussText(name) } : x));
    })();
    ladeBriefVorlagen().then((v) => { if (lebt) setVorlagen(v); }).catch(() => { /* Vorlagen sind optional */ });
    return () => { lebt = false; };
  }, []);

  const empfaengerWaehlen = async (t: EmpfaengerTreffer) => {
    setFehler(null);
    try {
      const e = await ladeEmpfaenger(t);
      setEmpf(e);
      setD((x) => ({ ...x, empfaenger: e.anschrift }));
      if (e.objektId) setObjektId(e.objektId);
    } catch (err) { setFehler((err as Error).message); }
  };

  // Vorlage übernehmen: Platzhalter mit den Empfänger-Daten (ohne Empfänger bleiben sie als {{…}} sichtbar).
  const vorlageUebernehmen = (id: string, mit: EmpfaengerDaten | null) => {
    setVorlageId(id);
    const v = vorlagen.find((x) => x.id === id);
    if (!v) return;
    const r = vorlageZuBrief(v.html_template, mit?.werte ?? {});
    setD((x) => ({ ...x, betreff: r.betreff || x.betreff, text: r.text }));
  };
  // Wird der Empfänger nach der Vorlage gewählt, die Vorlage mit seinen Daten neu füllen.
  const vorlageRef = useRef(vorlageUebernehmen);
  vorlageRef.current = vorlageUebernehmen;
  useEffect(() => { if (vorlageId && empf) vorlageRef.current(vorlageId, empf); }, [empf]); // eslint-disable-line react-hooks/exhaustive-deps
  // Startvorlage übernehmen, sobald die Vorlagen geladen sind.
  const gestartet = useRef(false);
  useEffect(() => {
    if (gestartet.current || !startVorlageId || !vorlagen.length) return;
    gestartet.current = true;
    vorlageRef.current(startVorlageId, null);
  }, [startVorlageId, vorlagen]);

  // Ablage: Objekte laden, Ordnerbaum des gewählten Objekts, Vorschlag für den Zielordner.
  useEffect(() => {
    if (!ablage || objekte.length) return;
    ladeObjekte().then(setObjekte).catch((e: unknown) => setFehler((e as Error).message));
  }, [ablage, objekte.length]);
  useEffect(() => {
    setBaum(null); setOrdnerPfad('');
    if (!ablage || !objektId) return;
    let lebt = true;
    ladeOrdnerBaum(objektId).then((b) => {
      if (!lebt) return;
      setBaum(b);
      setOrdnerPfad(vorschlagOrdner(b, empf?.art ?? null, empf?.ordnerName ?? null)?.pfad ?? '');
    }).catch((e: unknown) => { if (lebt) setFehler((e as Error).message); });
    return () => { lebt = false; };
  }, [ablage, objektId, empf]);
  const flach = useMemo(() => (baum ? flacheOrdner(baum) : []), [baum]);
  const ordnerIdNachPfad = useMemo(() => {
    const m = new Map<string, string>();
    const lauf = (l: readonly BaumKnoten[]) => l.forEach((k) => { m.set(k.pfad, k.gdriveId); lauf(k.kinder); });
    if (baum) lauf(baum);
    return m;
  }, [baum]);
  const objektOptionen: ObjektPickerOption[] = useMemo(() => objekte.map((o) => ({
    id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '), suchtext: `${o.nummer} ${o.ort}`,
    hinweis: o.driveId ? undefined : 'kein Drive-Ordner',
  })), [objekte]);

  const erzeuge = async () => erzeugeBlankoBriefPdf(d, await ladeBriefpapier());

  const zeigeVorschau = async () => {
    setLaeuft(true); setFehler(null);
    try { setVorschau(URL.createObjectURL((await erzeuge()).output('blob'))); }
    catch (e) { setFehler((e as Error).message); }
    finally { setLaeuft(false); }
  };

  const herunterladen = async () => {
    setLaeuft(true); setFehler(null);
    try { (await erzeuge()).save(briefDateiname(d)); onMeldung('Brief als PDF gespeichert'); }
    catch (e) { setFehler((e as Error).message); }
    finally { setLaeuft(false); }
  };

  const ordnerId = ordnerIdNachPfad.get(ordnerPfad) ?? '';
  const ablegen = async () => {
    if (!ordnerId) return;
    setLaeuft(true); setFehler(null);
    try {
      const pdf = (await erzeuge()).output('blob');
      const name = briefDateiname(d);
      await legeBriefAb(pdf, name, ordnerId, empf ? { art: empf.art, id: empf.id } : null, d.betreff.trim() || name.replace(/\.pdf$/, ''));
      onMeldung(`„${name}“ in der Akte abgelegt`);
      setAblage(false);
    } catch (e) { setFehler((e as Error).message); }
    finally { setLaeuft(false); }
  };

  return (
    <DialogHuelle titel="Blanko-Brief" untertitel="Freier Text oder Vorlage auf dem Briefpapier – als PDF oder direkt in die Akte" icon={PencilSimpleLine}
      onSchliessen={onSchliessen} laeuft={laeuft} breite={vorschau ? 1080 : 640}
      fuss={
        <div className="flex w-full flex-wrap justify-end" style={{ gap: 8 }}>
          <ImmoButton icon={laeuft ? SpinnerGap : Eye} onClick={() => void zeigeVorschau()} disabled={laeuft}>Vorschau</ImmoButton>
          <ImmoButton icon={FolderSimplePlus} onClick={() => setAblage((a) => !a)} disabled={laeuft}>In Akte ablegen</ImmoButton>
          <ImmoButton art="akzent" icon={DownloadSimple} onClick={() => void herunterladen()} disabled={laeuft}>PDF herunterladen</ImmoButton>
        </div>
      }>
      <div className="grid" style={{ gap: 16, gridTemplateColumns: vorschau ? 'minmax(0,1fr) minmax(0,1fr)' : 'minmax(0,1fr)' }}>
        <div className="flex flex-col" style={{ gap: 12 }}>
          <div className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Empfänger</span>
            {empf ? (
              <div className="flex items-center" style={{ gap: 8, padding: '6px 6px 6px 12px', borderRadius: 14, background: 'var(--acc-soft)' }}>
                <UserCircle size={18} weight="fill" color="var(--acc)" />
                <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13.5, fontWeight: 700 }}>{empf.name} <span style={{ fontWeight: 600, color: 'var(--ink3)' }}>· {ART_LABEL[empf.art]}</span></span>
                <button type="button" aria-label="Empfänger lösen" data-tip="Anderen Empfänger wählen" onClick={() => setEmpf(null)}
                  className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--ink2)', cursor: 'pointer' }}>
                  <X size={13} weight="bold" />
                </button>
              </div>
            ) : <EmpfaengerSuche onWahl={(t) => void empfaengerWaehlen(t)} />}
            <textarea rows={4} value={d.empfaenger} onChange={(e) => setze('empfaenger')(e.target.value)} aria-label="Anschrift"
              placeholder={'Anschrift (max. 6 Zeilen) – wird bei der Auswahl gefüllt, bleibt änderbar\nHerrn Max Mustermann\nMusterstraße 1\n12345 Musterstadt'} style={FELD} />
          </div>

          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Vorlage (optional)</span>
            <select value={vorlageId} onChange={(e) => (e.target.value ? vorlageUebernehmen(e.target.value, empf) : setVorlageId(''))}
              style={{ ...FELD, height: 44, padding: '0 12px', cursor: 'pointer' }}>
              <option value="">Freier Text</option>
              {vorlagen.map((v) => <option key={v.id} value={v.id}>{v.name} ({kategorieLabel(v.kategorie)})</option>)}
            </select>
            {vorlageId && (
              <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
                Platzhalter sind mit den Daten {empf ? `von ${empf.name}` : 'des Empfängers'} gefüllt; was fehlt, steht noch als {'{{…}}'} im Text. Alles bleibt frei änderbar.
              </span>
            )}
          </label>

          <FeldRaster minBreite={150}>
            <TextFeld label="Ort" wert={d.ort} onChange={setze('ort')} kompakt />
            <TextFeld label="Datum" type="date" wert={d.datum} onChange={setze('datum')} kompakt />
          </FeldRaster>
          <TextFeld label="Betreff" wert={d.betreff} onChange={setze('betreff')} placeholder="z. B. Ihre Wohnung Musterstraße 1, EG links" />
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Text</span>
            <textarea rows={14} value={d.text} onChange={(e) => setze('text')(e.target.value)} style={FELD} />
          </label>

          {ablage && (
            <section className="flex flex-col" aria-label="In Akte ablegen" style={{ gap: 10, padding: 12, borderRadius: 16, background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
              <span style={{ fontSize: 13, fontWeight: 800 }}>In Akte ablegen</span>
              <div className="flex flex-col" style={{ gap: 6 }}>
                <span style={LABEL}>Objekt</span>
                <div><ObjektPicker optionen={objektOptionen} wert={objektId} onWahl={setObjektId} /></div>
              </div>
              <div className="flex flex-col" style={{ gap: 6 }}>
                <span style={LABEL}>Zielordner</span>
                <OrdnerAuswahl optionen={flach} wert={ordnerPfad} onWahl={setOrdnerPfad} disabled={!objektId || !baum || laeuft}
                  platzhalter={!objektId ? 'Zuerst ein Objekt wählen' : !baum ? 'Ordner werden geladen …' : 'Ordner wählen …'} />
              </div>
              <div className="flex justify-end">
                <ImmoButton art="akzent" icon={FolderSimplePlus} onClick={() => void ablegen()} disabled={laeuft || !ordnerId}>
                  {laeuft ? 'Wird abgelegt …' : 'Ablegen'}
                </ImmoButton>
              </div>
            </section>
          )}

          {fehler && (
            <span role="alert" className="flex items-start" style={{ gap: 6, fontSize: 12.5, color: 'var(--bad)' }}>
              <WarningCircle weight="fill" size={14} style={{ flexShrink: 0, marginTop: 1 }} />{fehler}
            </span>
          )}
        </div>
        {vorschau && (
          <iframe title="Vorschau Brief" src={vorschau} className="w-full" style={{ minHeight: 560, border: 'none', borderRadius: 14, background: '#fff' }} />
        )}
      </div>
    </DialogHuelle>
  );
}
